import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal"
import { IMultilingualContent, ISimpleEventDetail, SupportedLanguage } from "interfaces";
import { get } from "lodash";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { languageSchema } from "routes/event/schema";
import { getDefaultEventLanguageValue } from "utills/helpers";
import { z } from "zod";

interface IDuplicateTicketModalProps extends IModalProps {
  onSubmit: (data: any) => void;
  eventDetailData: ISimpleEventDetail;
}

const DuplicateTicketSchema = (languages: SupportedLanguage[]) => {
  return z.object({
    category: languageSchema(languages),
  })
};

interface IAddTicketForm {
  category: IMultilingualContent;
}

export const DuplicateParentTicketModal = ({onClose, open, onSubmit, eventDetailData}: IDuplicateTicketModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IAddTicketForm>({
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        DuplicateTicketSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },    
  });
  const _submit = (values: IAddTicketForm) => {
    const submitValues = { ...values };
    onSubmit(submitValues);
  };

  useEffect(() => {
    if (open) {
      reset({
        category: getDefaultEventLanguageValue(eventDetailData.languages)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  
  return (
    <Modal title="Please enter the new category name when duplicating" onClose={onClose} open={open} closeOnClickBackdrop={false}>
      <div className="flex flex-col gap-y-4 box-container w-full">
        <form onSubmit={handleSubmit(_submit)} className="grid sm:grid-cols-6 gap-y-4 gap-x-4">
          <div className="col-span-full">
            <Label
              required
              id={`category_${eventDetailData.languages[0] ?? "en"}`}
              name="Category"
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map((lang) => (
                <div key={lang}>
                  <div className="relative" key={lang}>
                    <Input
                      id={`category_${lang}`}
                      type="text"
                      className="pr-11"
                      placeholder={`Cateogry (${lang})`}
                      {...register(`category.${lang}`)}
                      error={get(
                        errors,
                        `category.${lang}.message`,
                        ""
                      )}
                    />
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                  {get(
                    errors,
                    `category.${lang}`,
                    ""
                  ) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(
                          errors,
                          `category.${lang}.message`,
                          ""
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-full flex items-center gap-x-4 mt-4">
            <div className="flex-1">
              <Button
                type="button"
                data-autofocus
                onClick={onClose}
                className="inline-flex w-full justify-center bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 active:bg-gray-100 focus:ring-gray-300"
              >
                Cancel
              </Button>
            </div>
            <div className="flex-1">
              <Button type="submit" className="w-full">
                Duplicate
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}